/**
 * Settings → Webhooks (task #12).
 *
 * Send Docsight events (`document.classified`, `category.suggested`,
 * `processing.failed`, `retention.deleted`) to a tenant's own systems the
 * moment they happen — no polling. Each endpoint gets its own HMAC-SHA256
 * signing secret, revealed once at creation and again on rotate.
 *
 * Three sections: a KPI strip, the endpoints table (create / rotate / pause
 * / resume / delete), and a "recent deliveries" log. The backend only
 * exposes a delivery log *per endpoint* (`GET /v1/webhooks/{id}/deliveries`)
 * — there is no cross-endpoint feed to call — so the merged view here and
 * the KPI counts are both derived client-side from every endpoint's log
 * fetched in parallel.
 */
import { useCallback, useState } from 'react';

import type {
  IssuedWebhookEndpointView,
  WebhookDeliveryView,
  WebhookEndpointView,
} from '../../api/schema';
import { Button, ConfirmModal, EmptyState, ErrorState, KpiTile, Modal, Skeleton, useToast } from '../../components';
import { useLoadState } from '../../lib/useLoadState';
import { useSettingsApi } from './settingsApi';
import { reasonOf, relativeAge } from './format';
import './settings.css';

const EVENT_OPTIONS: readonly { event: string; description: string }[] = [
  { event: 'document.classified', description: 'A document finished processing and has a category.' },
  { event: 'category.suggested', description: 'The classifier proposed a new category worth reviewing.' },
  { event: 'processing.failed', description: "A document couldn't be OCR'd or classified after retries." },
  { event: 'retention.deleted', description: 'A document was deleted by retention policy or erasure.' },
];

const DAY_MS = 24 * 60 * 60 * 1000;

/** Deliveries older than this are dropped from the merged, per-endpoint feed
 * before display — enough for a "recent" log without an unbounded list. */
const MAX_DELIVERIES_SHOWN = 50;

const ENDPOINTS_GRID = '1.7fr 1.2fr 0.8fr 0.9fr 220px';
const DELIVERIES_GRID = '1.3fr 1.4fr 1fr 0.7fr 0.9fr 0.9fr';

interface WebhooksData {
  endpoints: WebhookEndpointView[];
  deliveries: WebhookDeliveryView[];
}

function statusLabel(status: WebhookEndpointView['status']): string {
  if (status === 'active') return 'Active';
  if (status === 'suspended') return 'Paused';
  return 'Archived';
}

function deliveryStatusLabel(status: WebhookDeliveryView['status']): string {
  if (status === 'delivered') return 'Delivered';
  if (status === 'pending') return 'Retrying';
  return 'Failed';
}

function deliveryDotClass(status: WebhookDeliveryView['status']): string {
  if (status === 'delivered') return 'set-dot set-dot--ok';
  if (status === 'pending') return 'set-dot set-dot--warn';
  return 'set-dot set-dot--bad';
}

function byNewest(a: WebhookDeliveryView, b: WebhookDeliveryView): number {
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

export function WebhooksScreen(): React.JSX.Element {
  const { toast } = useToast();
  const settingsApi = useSettingsApi();

  // Create-endpoint flow.
  const [createOpen, setCreateOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<Set<string>>(new Set(['document.classified']));
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<IssuedWebhookEndpointView | null>(null);

  // Row actions.
  const [rotatingId, setRotatingId] = useState<string | null>(null);
  const [pausingId, setPausingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<WebhookEndpointView | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const fetchWebhooks = useCallback(
    async (signal: AbortSignal): Promise<WebhooksData> => {
      const endpointsPage = await settingsApi.webhooks.list(signal);
      const endpoints = endpointsPage.items;
      const deliveryPages = await Promise.all(
        endpoints.map((endpoint) => settingsApi.webhooks.deliveries(endpoint.id, signal)),
      );
      const deliveries = deliveryPages.flatMap((page) => page.items).sort(byNewest);
      return { endpoints, deliveries };
    },
    [settingsApi],
  );
  const state = useLoadState(fetchWebhooks);
  const reload = state.status === 'ready' || state.status === 'error' ? state.reload : undefined;

  const copy = useCallback(
    (text: string) => {
      const nav = navigator as { clipboard?: Clipboard };
      if (nav.clipboard) void nav.clipboard.writeText(text).catch(() => undefined);
      toast('Copied to clipboard');
    },
    [toast],
  );

  const openCreate = useCallback(() => {
    setUrl('');
    setDescription('');
    setEvents(new Set(['document.classified']));
    setCreateOpen(true);
  }, []);

  const submitCreate = useCallback(() => {
    if (url.trim() === '' || events.size === 0) return;
    setCreating(true);
    void (async () => {
      try {
        const issued = await settingsApi.webhooks.create({
          url: url.trim(),
          events: [...events],
          description: description.trim() === '' ? null : description.trim(),
        });
        setCreateOpen(false);
        setRevealed(issued);
        toast('Endpoint created');
        reload?.();
      } catch (error) {
        toast(reasonOf(error, 'Could not create the endpoint — try again'), { tone: 'error' });
      } finally {
        setCreating(false);
      }
    })();
  }, [url, description, events, toast, reload, settingsApi]);

  const rotate = useCallback(
    (endpoint: WebhookEndpointView) => {
      setRotatingId(endpoint.id);
      void (async () => {
        try {
          const issued = await settingsApi.webhooks.rotate(endpoint.id);
          setRevealed(issued);
          toast('Secret rotated — update your integration with the new value');
          reload?.();
        } catch {
          toast('Could not rotate the secret — try again', { tone: 'error' });
        } finally {
          setRotatingId(null);
        }
      })();
    },
    [toast, reload, settingsApi],
  );

  const togglePause = useCallback(
    (endpoint: WebhookEndpointView) => {
      const next = endpoint.status === 'active' ? 'suspended' : 'active';
      setPausingId(endpoint.id);
      void (async () => {
        try {
          await settingsApi.webhooks.update(endpoint.id, { status: next });
          toast(next === 'active' ? 'Endpoint resumed' : 'Endpoint paused — deliveries stop until resumed');
          reload?.();
        } catch {
          toast('Could not update the endpoint — try again', { tone: 'error' });
        } finally {
          setPausingId(null);
        }
      })();
    },
    [toast, reload, settingsApi],
  );

  const confirmDelete = useCallback(() => {
    const endpoint = deleting;
    if (endpoint === null) return;
    setDeleteBusy(true);
    void (async () => {
      try {
        await settingsApi.webhooks.delete(endpoint.id);
        toast('Endpoint deleted');
        setDeleting(null);
        reload?.();
      } catch {
        toast('Could not delete the endpoint — try again', { tone: 'error' });
      } finally {
        setDeleteBusy(false);
      }
    })();
  }, [deleting, toast, reload, settingsApi]);

  if (state.status === 'loading') {
    return (
      <section className="set-wrap" aria-label="Webhooks">
        <Skeleton height={90} />
        <Skeleton height={200} />
        <Skeleton height={220} />
        <p className="set-loading-caption">Loading webhooks…</p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="set-wrap" aria-label="Webhooks">
        <ErrorState
          title="We couldn't load your webhooks"
          body="The settings service didn't respond. Existing deliveries keep running — this only affects this view."
          traceId={state.traceId ?? undefined}
          actions={
            <Button onClick={state.reload} variant="primary">
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  const { endpoints, deliveries } = state.data;
  const endpointLabel = (endpointId: string): string => {
    const endpoint = endpoints.find((row) => row.id === endpointId);
    if (endpoint === undefined) return '—';
    return endpoint.description != null && endpoint.description !== '' ? endpoint.description : endpoint.url;
  };
  const lastDeliveryFor = (endpointId: string): WebhookDeliveryView | undefined =>
    deliveries.find((delivery) => delivery.endpoint_id === endpointId);

  const activeCount = endpoints.filter((endpoint) => endpoint.status === 'active').length;
  const cutoff = Date.now() - DAY_MS;
  const recentDeliveries = deliveries.filter((delivery) => new Date(delivery.created_at).getTime() >= cutoff);
  const failedRecent = recentDeliveries.filter((delivery) => delivery.status === 'failed');

  return (
    <section className="set-wrap" aria-label="Webhooks">
      <div className="set-kpis">
        <KpiTile label="Active endpoints" value={`${String(activeCount)} of ${String(endpoints.length)}`} />
        <KpiTile label="Deliveries · 24h" value={String(recentDeliveries.length)} />
        <KpiTile label="Failed · 24h" value={String(failedRecent.length)} />
      </div>

      <div className="set-card">
        <div className="set-card__head">
          <h2 className="set-card__title">Endpoints</h2>
          <span className="set-card__aside">{endpoints.length} registered</span>
          <span className="set-card__spacer" />
          <Button variant="primary" size="sm" onClick={openCreate}>
            + Add endpoint
          </Button>
        </div>
        {endpoints.length === 0 ? (
          <EmptyState
            title="Send your first event"
            body="No endpoints yet. Register one to have Docsight push document events to your own systems."
            actions={
              <Button variant="primary" onClick={openCreate}>
                Add endpoint
              </Button>
            }
          />
        ) : (
          <div className="set-scroll">
            <div className="set-grid set-grid__head" role="row" style={{ gridTemplateColumns: ENDPOINTS_GRID }}>
              <span role="columnheader">Endpoint</span>
              <span role="columnheader">Events</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Last delivery</span>
              <span role="columnheader" className="sr-only">
                Actions
              </span>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Webhook endpoints">
              {endpoints.map((endpoint) => {
                const lastDelivery = lastDeliveryFor(endpoint.id);
                const active = endpoint.status === 'active';
                return (
                  <li className="set-grid set-grid__row" key={endpoint.id} style={{ gridTemplateColumns: ENDPOINTS_GRID }}>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                      <span
                        className="set-mono"
                        style={{ fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={endpoint.url}
                      >
                        {endpoint.url}
                      </span>
                      {endpoint.description != null && endpoint.description !== '' ? (
                        <span style={{ fontSize: 11, color: 'var(--t3)' }}>{endpoint.description}</span>
                      ) : null}
                    </span>
                    <span className="set-chips">
                      {endpoint.events.map((event) => (
                        <span className="set-chip" key={event}>
                          {event}
                        </span>
                      ))}
                    </span>
                    <span className="set-status">
                      <span className={`set-dot ${active ? 'set-dot--ok' : 'set-dot--idle'}`} aria-hidden="true" />
                      {statusLabel(endpoint.status)}
                    </span>
                    <span className="set-mono" style={{ color: 'var(--t3)' }}>
                      {lastDelivery ? relativeAge(lastDelivery.created_at) : '—'}
                    </span>
                    <span className="set-rowactions">
                      <button
                        type="button"
                        className="set-linkbtn"
                        disabled={rotatingId === endpoint.id}
                        onClick={() => {
                          rotate(endpoint);
                        }}
                      >
                        Rotate secret
                      </button>
                      <button
                        type="button"
                        className="set-linkbtn"
                        disabled={pausingId === endpoint.id}
                        onClick={() => {
                          togglePause(endpoint);
                        }}
                      >
                        {active ? 'Pause' : 'Resume'}
                      </button>
                      <button
                        type="button"
                        className="set-linkbtn set-linkbtn--bad"
                        onClick={() => {
                          setDeleting(endpoint);
                        }}
                      >
                        Delete
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <p className="set-card__foot">
          Every delivery is signed: <code className="set-mono">X-Docsight-Signature</code> is the hex HMAC-SHA256 of{' '}
          <code className="set-mono">{'{timestamp}.{body}'}</code> using the endpoint&apos;s secret — recompute it and
          reject anything that doesn&apos;t match or arrives more than 5 minutes late.
        </p>
      </div>

      <div className="set-card">
        <div className="set-card__head">
          <h2 className="set-card__title">Recent deliveries</h2>
          <span className="set-card__aside">across all endpoints</span>
        </div>
        {deliveries.length === 0 ? (
          <p className="set-hint">No deliveries yet — they&apos;ll show up here the moment a subscribed event fires.</p>
        ) : (
          <div className="set-scroll">
            <div className="set-grid set-grid__head" role="row" style={{ gridTemplateColumns: DELIVERIES_GRID }}>
              <span role="columnheader">Event</span>
              <span role="columnheader">Endpoint</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Attempts</span>
              <span role="columnheader">Response</span>
              <span role="columnheader">When</span>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Recent deliveries">
              {deliveries.slice(0, MAX_DELIVERIES_SHOWN).map((delivery) => (
                <li className="set-grid set-grid__row" key={delivery.id} style={{ gridTemplateColumns: DELIVERIES_GRID }}>
                  <span className="set-mono">{delivery.event}</span>
                  <span style={{ color: 'var(--t2)' }}>{endpointLabel(delivery.endpoint_id)}</span>
                  <span className="set-status">
                    <span className={deliveryDotClass(delivery.status)} aria-hidden="true" />
                    {deliveryStatusLabel(delivery.status)}
                  </span>
                  <span className="set-mono" style={{ color: 'var(--t3)' }}>
                    {delivery.attempts}
                  </span>
                  <span
                    className="set-mono"
                    style={{ color: delivery.status === 'failed' ? 'var(--bad)' : 'var(--t3)' }}
                  >
                    {delivery.status_code ?? delivery.last_error ?? '—'}
                  </span>
                  <span className="set-mono" style={{ color: 'var(--t3)' }}>
                    {relativeAge(delivery.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="set-card__foot">
          Every attempt is logged, delivered or not — nothing here is inferred. Failed rows keep retrying on a backoff
          schedule until the attempt cap, then stop and stay visible.
        </p>
      </div>

      {/* Create-endpoint modal ------------------------------------------------ */}
      <Modal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        title="Add endpoint"
        dismissible
        footer={
          <div className="ds-modal__footer">
            <Button
              variant="secondary"
              onClick={() => {
                setCreateOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={creating}
              disabled={url.trim() === '' || events.size === 0}
              onClick={submitCreate}
            >
              Create endpoint
            </Button>
          </div>
        }
      >
        <div className="ds-modal__body">
          <div className="ds-field">
            <label className="ds-field__label" htmlFor="wh-url">
              Endpoint URL
            </label>
            <input
              id="wh-url"
              className="ds-input"
              type="url"
              placeholder="https://your-system.example.com/hooks/docsight"
              value={url}
              onChange={(event) => {
                setUrl(event.currentTarget.value);
              }}
            />
            <span className="set-hint">Must be reachable over HTTPS from the public internet.</span>
          </div>
          <div className="ds-field" style={{ marginTop: 12 }}>
            <label className="ds-field__label" htmlFor="wh-desc">
              Description <span className="set-hint">(optional)</span>
            </label>
            <input
              id="wh-desc"
              className="ds-input"
              placeholder="e.g. Finance system sync"
              value={description}
              onChange={(event) => {
                setDescription(event.currentTarget.value);
              }}
            />
          </div>
          <fieldset style={{ border: 'none', margin: '14px 0 0', padding: 0 }}>
            <legend className="ds-field__label">Events to send</legend>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
              {EVENT_OPTIONS.map(({ event, description: eventDescription }) => {
                const inputId = `wh-event-${event.replace(/\./g, '-')}`;
                return (
                  <label
                    key={event}
                    htmlFor={inputId}
                    className={`set-optrow${events.has(event) ? ' set-optrow--on' : ''}`}
                  >
                    <input
                      id={inputId}
                      type="checkbox"
                      aria-label={event}
                      checked={events.has(event)}
                      onChange={(changeEvent) => {
                        const checked = changeEvent.currentTarget.checked;
                        setEvents((prev) => {
                          const next = new Set(prev);
                          if (checked) next.add(event);
                          else next.delete(event);
                          return next;
                        });
                      }}
                    />
                    <span>
                      <span className="set-mono set-optrow__name">{event}</span>
                      <span className="set-optrow__desc">{eventDescription}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>
      </Modal>

      {/* Reveal-once secret modal — create + rotate share it ------------------ */}
      <Modal
        open={revealed !== null}
        onClose={() => {
          setRevealed(null);
        }}
        title="Signing secret — copy it now"
        footer={
          <div className="ds-modal__footer">
            <Button
              variant="primary"
              onClick={() => {
                setRevealed(null);
              }}
            >
              I&apos;ve stored it safely
            </Button>
          </div>
        }
      >
        <div className="ds-modal__body">
          <div className="set-banner" role="status" style={{ marginTop: 0 }}>
            This is the <strong>only time</strong> the secret is shown. If you lose it, rotate the endpoint to get a
            new one.
          </div>
          {revealed !== null ? (
            <div
              style={{
                background: 'var(--t1)',
                borderRadius: 10,
                padding: '12px 14px',
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <code
                className="set-mono"
                style={{ fontSize: 12, color: 'var(--bg)', wordBreak: 'break-all', flex: 1 }}
                data-testid="revealed-secret"
              >
                {revealed.secret}
              </code>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  copy(revealed.secret);
                }}
              >
                Copy
              </Button>
            </div>
          ) : null}
        </div>
      </Modal>

      {/* Delete confirm --------------------------------------------------------- */}
      <ConfirmModal
        open={deleting !== null}
        onCancel={() => {
          setDeleting(null);
        }}
        onConfirm={confirmDelete}
        title="Delete this endpoint?"
        destructive
        confirmLabel="Delete endpoint"
        cancelLabel="Keep it"
        loading={deleteBusy}
      >
        No further events are delivered to <code className="set-mono">{deleting?.url ?? ''}</code>. Its delivery log
        remains. This can&apos;t be undone.
      </ConfirmModal>
    </section>
  );
}
